import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Separator,
} from "@pushpress/pushpress-ui";
import { useExperiment } from "../../../hooks/useExperiment";
import { PlanTemplateContainer } from "./PlanTemplateContainer";
import { ChevronRight } from "lucide-react";
import { usePlans } from "../PlansProvider/PlansProvider";
import { PlanType } from "src/apis/subscriptionApi";

export interface PlanTypeWithTemplatesModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type PlanTypeConfig = {
  title: string;
  type: PlanType;
  description: string;
};

const planTypes: PlanTypeConfig[] = [
  {
    title: "Recurring",
    type: "recurring",
    description:
      "Ongoing access for one or more of classes, appointments, and open gym. Recurring billing with optional end date.",
  },
  {
    title: "Non-recurring",
    type: "non-recurring",
    description:
      "Commonly used for pay-in-full options, or bootcamps, courses etc.",
  },
  {
    title: "Punchcard",
    type: "session-pack",
    description: "Sessions that can be used for classes and / or open gym.",
  },
];

const PlanTypeCard = ({
  title,
  description,
  onClick,
}: {
  title: string;
  description: string;
  onClick: () => void;
}) => {
  return (
    <button
      className="tw-p-6 tw-border-gray-200 tw-border tw-bg-gray-50 tw-rounded-lg tw-text-left tw-flex tw-flex-col hover:tw-shadow-md tw-transition-shadow"
      onClick={onClick}
    >
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
        <h3 className="tw-text-base tw-text-lg tw-font-medium">{title}</h3>
        <ChevronRight className="tw-text-gray-600 tw-w-5 tw-h-5" />
      </div>
      <p className="tw-text-gray-600 tw-text-sm">{description}</p>
    </button>
  );
};

export const PlanTypeWithTemplatesModal: React.FC<
  PlanTypeWithTemplatesModalProps
> = ({ visible, onClose, onSuccess }) => {
  const { openPlanDrawer } = usePlans();

  const handlePlanTypeClick = (planType: PlanType) => {
    openPlanDrawer({ planType, onSuccess });

    onClose();
  };

  const { useExperimentValue } = useExperiment();

  const isCreatePlanModalEnabled = useExperimentValue(
    "plan-v2-feature_flag",
    false
  );

  const modalStyle = isCreatePlanModalEnabled ? 
    "tw-w-full tw-min-w-[100vw] tw-h-full tw-overflow-y-auto" : 
    "tw-w-full tw-min-w-[70vw] tw-max-h-[80vh] tw-overflow-y-auto";

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent
        className={modalStyle}
        align="center"
        aria-describedby="new-plan-description"
      >
        <DialogHeader className="tw-sr-only">
          <DialogTitle>New Plan</DialogTitle>
        </DialogHeader>
        <div className="tw-p-3 tw-flex tw-flex-col tw-gap-4">
          <p className="tw-text-xl">New Plan</p>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-mb-8">
            {planTypes.map((type) => (
              <PlanTypeCard
                key={type.title}
                {...type}
                onClick={() => handlePlanTypeClick(type.type)}
              />
            ))}
          </div>
          <Separator />
          <PlanTemplateContainer onClose={onClose} onSuccess={onSuccess} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
